import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { APP_ROUTES } from "../constants";
import {
  fetchMaintenanceStatus,
  getWidgetLinkId,
  MAINTENANCE_EVENT,
  type MaintenanceStatus,
} from "../services/api";
import { useSessionStore } from "../store/useSessionStore";
import { RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { resolveXLinkIdHeader } from "../services/resolveXLinkIdHeader";

const IDLE_POLL_MS = 45_000;

export function maintenanceLinkId(): string | null {
  return resolveXLinkIdHeader({
    urlLinkId: resolveUrlLinkId(),
    currentLinkId: getWidgetLinkId(),
    persistedLinkId: useSessionStore.getState().session.data?.linkId ?? null,
  });
}

export function readMaintenanceDetail(detail: unknown): MaintenanceStatus {
  if (!detail || typeof detail !== "object") return { active: true };
  const raw = detail as Record<string, unknown>;
  return {
    active: true,
    title: typeof raw.title === "string" ? raw.title : undefined,
    body:
      typeof raw.body === "string"
        ? raw.body
        : typeof raw.message === "string"
          ? raw.message
          : undefined,
    supportEmail: typeof raw.supportEmail === "string" ? raw.supportEmail : undefined,
    supportWhatsappNumber:
      typeof raw.supportWhatsappNumber === "string"
        ? raw.supportWhatsappNumber
        : undefined,
  };
}

/**
 * Routes a maintenance 503 to the block screen, and polls while the tab is
 * visible so an idle session still stops. A failed poll stays on the current
 * page.
 */
export function useMaintenanceWatch() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const onMaintenance = (event: Event) => {
      const detail = readMaintenanceDetail(
        (event as CustomEvent<unknown>).detail,
      );
      if (location.pathname === APP_ROUTES.MAINTENANCE_PAGE) return;
      navigate(APP_ROUTES.MAINTENANCE_PAGE, { replace: true, state: detail });
    };
    window.addEventListener(MAINTENANCE_EVENT, onMaintenance);
    return () => window.removeEventListener(MAINTENANCE_EVENT, onMaintenance);
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (location.pathname === APP_ROUTES.MAINTENANCE_PAGE) return;

    let stopped = false;
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      const linkId = maintenanceLinkId();
      if (!linkId) return;
      if (useSessionStore.getState().session.status === RequestStatus.LOADING) {
        return;
      }
      void fetchMaintenanceStatus(linkId, i18n.language)
        .then((status) => {
          if (stopped || !status.active) return;
          navigate(APP_ROUTES.MAINTENANCE_PAGE, { replace: true, state: status });
        })
        .catch(() => {
          // Fail open. A dead link is already routed by the axios interceptor.
        });
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    const timer = window.setInterval(tick, IDLE_POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [i18n.language, location.pathname, navigate]);
}
